import { Temporal } from 'temporal-polyfill';

/**
 * VTIMEZONE generation.
 *
 * A recurring class is a wall-clock commitment: "6pm every Tuesday" must stay 6pm
 * across a DST change. Expressing that in iCalendar requires
 * `DTSTART;TZID=America/Los_Angeles` plus a VTIMEZONE that defines the zone's
 * offsets. Emitting UTC instants instead would silently shift the class by an
 * hour for half the year.
 *
 * Offsets come from Temporal's own transition data rather than a bundled zone
 * table, so they always agree with how the app itself computes times.
 */

/** How far ahead an open-ended series' transitions are enumerated. */
const HORIZON_YEARS = 5;

export interface TimeZoneWindow {
  readonly timeZone: string;
  readonly from: Temporal.PlainDate;
  readonly to: Temporal.PlainDate;
}

interface Onset {
  /** Local wall-clock time at which the new offset takes effect. */
  readonly at: Temporal.PlainDateTime;
  readonly offsetFrom: string;
  readonly offsetTo: string;
}

function basicOffset(offset: string): string {
  // "-08:00" -> "-0800"; iCalendar uses the basic form.
  return offset.replace(':', '');
}

function offsetMinutes(offset: string): number {
  const match = /^([+-])(\d{2}):(\d{2})$/u.exec(offset);
  if (match === null) {
    return 0;
  }
  const sign = match[1] === '-' ? -1 : 1;
  return sign * (Number(match[2]) * 60 + Number(match[3]));
}

export function formatLocalDateTime(value: Temporal.PlainDateTime): string {
  const pad = (n: number, width = 2): string => String(n).padStart(width, '0');
  return `${pad(value.year, 4)}${pad(value.month)}${pad(value.day)}T${pad(value.hour)}${pad(
    value.minute,
  )}${pad(value.second)}`;
}

/** Every offset change in the window, in chronological order. */
export function collectOnsets(span: TimeZoneWindow): Onset[] {
  const onsets: Onset[] = [];
  let cursor: Temporal.ZonedDateTime | null = span.from.toZonedDateTime({
    timeZone: span.timeZone,
    plainTime: Temporal.PlainTime.from('00:00'),
  });
  const limit = span.to.toZonedDateTime({
    timeZone: span.timeZone,
    plainTime: Temporal.PlainTime.from('23:59'),
  });

  while (cursor !== null) {
    const next: Temporal.ZonedDateTime | null = cursor.getTimeZoneTransition('next');
    if (next === null || Temporal.ZonedDateTime.compare(next, limit) > 0) {
      break;
    }
    // One second before the transition still carries the outgoing offset.
    const before = next.subtract({ seconds: 1 });
    onsets.push({
      at: next.toPlainDateTime(),
      offsetFrom: before.offset,
      offsetTo: next.offset,
    });
    cursor = next;
  }

  return onsets;
}

/**
 * Which weekday-of-month an onset falls on, preferring "last" when applicable.
 *
 * DST rules are expressed as "second Sunday in March" or "last Sunday in
 * October", so recognising the -1 case is what lets a single yearly RRULE cover
 * the zone indefinitely.
 */
export function nthWeekdayOfDate(date: Temporal.PlainDateTime): number {
  const withinMonth = Math.ceil(date.day / 7);
  const isLast = date.day + 7 > date.daysInMonth;
  return isLast ? -1 : withinMonth;
}

const ICAL_WEEKDAYS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

export function icalWeekday(isoDayOfWeek: number): string {
  return ICAL_WEEKDAYS[isoDayOfWeek - 1] ?? 'MO';
}

function yearlyRule(onset: Onset): string {
  const nth = nthWeekdayOfDate(onset.at);
  return `RRULE:FREQ=YEARLY;BYMONTH=${onset.at.month};BYDAY=${nth}${icalWeekday(
    onset.at.dayOfWeek,
  )}`;
}

/** True when every onset in the group follows the same yearly pattern. */
function followsSameRule(group: readonly Onset[]): boolean {
  const first = group[0];
  if (first === undefined) {
    return false;
  }
  const month = first.at.month;
  const weekday = first.at.dayOfWeek;
  const nth = nthWeekdayOfDate(first.at);
  const time = first.at.toPlainTime().toString();

  return group.every(
    (onset) =>
      onset.at.month === month &&
      onset.at.dayOfWeek === weekday &&
      nthWeekdayOfDate(onset.at) === nth &&
      onset.at.toPlainTime().toString() === time &&
      onset.offsetFrom === first.offsetFrom &&
      onset.offsetTo === first.offsetTo,
  );
}

function onsetComponent(kind: 'STANDARD' | 'DAYLIGHT', group: readonly Onset[]): string[] {
  const first = group[0];
  if (first === undefined) {
    return [];
  }

  const lines = [
    `BEGIN:${kind}`,
    `DTSTART:${formatLocalDateTime(first.at)}`,
    `TZOFFSETFROM:${basicOffset(first.offsetFrom)}`,
    `TZOFFSETTO:${basicOffset(first.offsetTo)}`,
  ];

  if (group.length > 1) {
    if (followsSameRule(group)) {
      // A yearly rule keeps the zone correct beyond the enumerated horizon.
      lines.push(yearlyRule(first));
    } else {
      // The rule changed mid-window, so list the remaining onsets explicitly.
      const rest = group.slice(1).map((onset) => formatLocalDateTime(onset.at));
      lines.push(`RDATE:${rest.join(',')}`);
    }
  }

  lines.push(`END:${kind}`);
  return lines;
}

/**
 * A VTIMEZONE for one zone covering the given span.
 *
 * TZNAME is deliberately omitted. It is optional in RFC 5545, and deriving it
 * would mean relying on Intl's locale data, which varies by runtime and would
 * make the output non-deterministic.
 */
export function buildVTimeZone(span: TimeZoneWindow): string[] {
  const onsets = collectOnsets({
    ...span,
    // Scanning a year earlier guarantees the first onset precedes the first
    // event. Without it, a parser has no offset defined for the earliest
    // occurrence and silently falls back to UTC — shifting the class by hours.
    from: span.from.subtract({ years: 1 }),
    to: span.to.add({ years: HORIZON_YEARS }),
  });

  if (onsets.length === 0) {
    // A fixed-offset zone still needs a VTIMEZONE for the TZID to resolve.
    const offset = span.from.toZonedDateTime({
      timeZone: span.timeZone,
      plainTime: Temporal.PlainTime.from('00:00'),
    }).offset;
    return [
      'BEGIN:VTIMEZONE',
      `TZID:${span.timeZone}`,
      'BEGIN:STANDARD',
      'DTSTART:19700101T000000',
      `TZOFFSETFROM:${basicOffset(offset)}`,
      `TZOFFSETTO:${basicOffset(offset)}`,
      'END:STANDARD',
      'END:VTIMEZONE',
    ];
  }

  // Of the two alternating offsets, the larger one is daylight saving time.
  const offsets = [...new Set(onsets.map((onset) => onset.offsetTo))];
  const maxOffset = Math.max(...offsets.map(offsetMinutes));

  const daylight = onsets.filter((onset) => offsetMinutes(onset.offsetTo) === maxOffset);
  const standard = onsets.filter((onset) => offsetMinutes(onset.offsetTo) !== maxOffset);

  return [
    'BEGIN:VTIMEZONE',
    `TZID:${span.timeZone}`,
    ...onsetComponent('STANDARD', standard),
    ...onsetComponent('DAYLIGHT', daylight),
    'END:VTIMEZONE',
  ];
}
